// The boat's notebook on the Equipment page (TOM-610, cloud ADR 0370, ADR
// 0373, api_contracts §5.21): what Neo knows about the boat, from its readings
// and from its owners, and what an owner does with a note: confirm it, correct
// it in their own words, forget it, or undo their own last confirm or correct.
// The notebook is the boat's, shared by every owner of it.
//
// Neo speaks with one voice here: the page never names which expert noticed a
// note, and the read hands out no such thing.

import { api } from '../api';
import type { BoatOwner } from './boatOwners';
import type { EquipmentEntry } from './equipment';

export const BOAT_NOTE_AREAS = [
  'electrical',
  'mechanical',
  'electronics',
  'boards',
  'general',
] as const;
export type BoatNoteArea = (typeof BOAT_NOTE_AREAS)[number];

/** The statuses the page lists; a forgotten note has no words to show. */
export type BoatNoteStatus = 'active' | 'contradicted' | 'stale' | 'set_aside';

export const BOAT_NOTE_TEXT_MAX = 240;

export const BOAT_NOTE_KINDS = [
  'identity',
  'wiring',
  'rating',
  'normal_range',
  'habit',
  'quirk',
] as const;
export type BoatNoteKind = (typeof BOAT_NOTE_KINDS)[number];

/**
 * The sorts of fact, as the page asks for them when an owner adds a note. The
 * notebook keeps one note of each sort on each thing, so an owner with two
 * facts about the house bank files them as two sorts. "What it is" comes
 * first: what an owner knows and the readings cannot show.
 */
export const NOTE_KIND_CHOICES: ReadonlyArray<{ kind: BoatNoteKind; label: string }> = [
  { kind: 'identity', label: 'What it is' },
  { kind: 'wiring', label: 'How it’s wired or connected' },
  { kind: 'rating', label: 'Its size or rating' },
  { kind: 'normal_range', label: 'Its normal readings' },
  { kind: 'habit', label: 'How the boat uses it' },
  { kind: 'quirk', label: 'A quirk to know about' },
];

/** One note, as `GET …/notebook` lists it. */
export interface BoatNote {
  id: string;
  area: BoatNoteArea;
  subject: string | null;
  kind: string;
  text: string;
  origin: 'observed' | 'owner';
  status: BoatNoteStatus;
  confirmedBy: string | null;
  confirmedAt: string | null;
  observedAt: string;
  lastCheckedAt: string;
  disputedAt: string | null;
  authorUser: string | null;
  /** An owner's note whose author no longer owns the boat: shown as "a former owner", never by name. */
  authorIsFormerOwner: boolean;
  version: number;
}

// ----- Reading ---------------------------------------------------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const AREAS = new Set<string>(BOAT_NOTE_AREAS);
const STATUSES = new Set<string>(['active', 'contradicted', 'stale', 'set_aside']);

function optionalString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/** One listed note, or null when it is not one (a forgotten note's tombstone included). */
export function parseBoatNote(raw: unknown): BoatNote | null {
  if (!isObject(raw)) return null;
  const { id, area, kind, text, origin, status, observedAt, lastCheckedAt, version } = raw;
  if (typeof id !== 'string' || id === '') return null;
  if (typeof area !== 'string' || !AREAS.has(area)) return null;
  if (typeof kind !== 'string') return null;
  if (typeof text !== 'string' || text === '') return null;
  if (origin !== 'observed' && origin !== 'owner') return null;
  if (typeof status !== 'string' || !STATUSES.has(status)) return null;
  if (typeof observedAt !== 'string' || typeof lastCheckedAt !== 'string') return null;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) return null;
  return {
    id,
    area: area as BoatNoteArea,
    subject: optionalString(raw.subject),
    kind,
    text,
    origin,
    status: status as BoatNoteStatus,
    confirmedBy: optionalString(raw.confirmedBy),
    confirmedAt: optionalString(raw.confirmedAt),
    observedAt,
    lastCheckedAt,
    disputedAt: optionalString(raw.disputedAt),
    authorUser: optionalString(raw.authorUser),
    // Read as former unless the cloud says otherwise: never a name by mistake.
    authorIsFormerOwner: raw.authorIsFormerOwner !== false,
    version,
  };
}

/** `GET …/notebook`, or null when the body is not one. A note it cannot read is skipped. */
export function parseBoatNotebook(raw: unknown): BoatNote[] | null {
  if (!isObject(raw) || !Array.isArray(raw.notes)) return null;
  return raw.notes.flatMap((n) => {
    const note = parseBoatNote(n);
    return note === null ? [] : [note];
  });
}

export async function fetchBoatNotebook(boatId: string): Promise<BoatNote[]> {
  const notes = parseBoatNotebook(await api(`/v1/boats/${boatId}/notebook`));
  if (notes === null) throw new Error('notebook: unexpected body');
  return notes;
}

// ----- Changing a note -------------------------------------------------------------------

export type BoatNoteAction = 'confirm' | 'correct' | 'forget' | 'undo';

export type BoatNoteChangeResult =
  /**
   * The note as it is now; null after a forget, which leaves no words to show,
   * and after the undo of an add, which takes the note away.
   */
  | { kind: 'saved'; note: BoatNote | null }
  /** `409 version_conflict`: another owner changed it first; read the notebook again. */
  | { kind: 'conflict' }
  /** A refusal the page words: the note's words, a full notebook, nothing to undo, or gone. */
  | { kind: 'refused'; code: string }
  | { kind: 'failed' };

/**
 * Confirm, correct, forget or undo one note at the version the page shows.
 * The caller passes a fresh idempotency key for each change (the page makes
 * one per click and never resends): the cloud keys it on this note's own path,
 * so a key never answers for another note (TOM-610).
 */
export async function changeBoatNote(
  boatId: string,
  note: Pick<BoatNote, 'id' | 'version'>,
  action: BoatNoteAction,
  idempotencyKey: string,
  text?: string
): Promise<BoatNoteChangeResult> {
  try {
    const raw = await api(`/v1/boats/${boatId}/notebook/${note.id}/${action}`, {
      method: 'POST',
      body:
        action === 'correct'
          ? { text, expectedVersion: note.version }
          : { expectedVersion: note.version },
      idempotencyKey,
    });
    if (action === 'forget') return { kind: 'saved', note: null };
    if (action === 'undo' && isObject(raw) && raw.note === null)
      return { kind: 'saved', note: null };
    const saved = isObject(raw) ? parseBoatNote(raw.note) : null;
    return saved === null ? { kind: 'failed' } : { kind: 'saved', note: saved };
  } catch (err) {
    return refusalOf(err);
  }
}

/** A failed change as the page tells it: a stale version, a refusal it words, or a failure. */
function refusalOf(err: unknown): Exclude<BoatNoteChangeResult, { kind: 'saved' }> {
  const status = isObject(err) && typeof err.status === 'number' ? err.status : null;
  const body = isObject(err) && isObject(err.body) ? err.body : null;
  const code = body !== null && typeof body.error === 'string' ? body.error : null;
  if (status === 409 && code === 'version_conflict') return { kind: 'conflict' };
  if ((status === 409 || status === 422 || status === 404) && code !== null) {
    return { kind: 'refused', code };
  }
  return { kind: 'failed' };
}

/** What an owner adds: a topic, what it is about (an equipment key, or none), the sort of fact, and the words. */
export interface NewBoatNote {
  area: BoatNoteArea;
  subject: string | null;
  kind: BoatNoteKind;
  text: string;
}

/**
 * Add a note in the owner's own words. The caller passes a fresh idempotency
 * key for each add; the page makes one per click and never resends.
 */
export async function addBoatNoteOnPage(
  boatId: string,
  input: NewBoatNote,
  idempotencyKey: string
): Promise<BoatNoteChangeResult> {
  try {
    const raw = await api(`/v1/boats/${boatId}/notebook`, {
      method: 'POST',
      body: input,
      idempotencyKey,
    });
    const saved = isObject(raw) ? parseBoatNote(raw.note) : null;
    return saved === null ? { kind: 'failed' } : { kind: 'saved', note: saved };
  } catch (err) {
    return refusalOf(err);
  }
}

// ----- Words ------------------------------------------------------------------------------

/** The page's topics, in its order, each one area of the boat. */
export const NOTE_TOPICS: ReadonlyArray<{ area: BoatNoteArea; heading: string }> = [
  { area: 'electrical', heading: 'Batteries and charging' },
  { area: 'mechanical', heading: 'Engines' },
  { area: 'electronics', heading: 'Instruments and network' },
  { area: 'boards', heading: 'Boards' },
  { area: 'general', heading: 'General' },
];

/** "Oct 3", or "Oct 3, 2025" outside this year. */
export function noteDay(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/**
 * An owner as a note names them: their first name, "you" when you have no
 * name, "an owner" otherwise. Null when they no longer own the boat.
 */
export function ownerWord(
  userId: string | null,
  owners: readonly BoatOwner[] | null
): string | null {
  if (owners === null) return 'an owner';
  const owner = userId === null ? undefined : owners.find((o) => o.userId === userId);
  if (owner === undefined) return null;
  const first = owner.displayName?.trim().split(/\s+/)[0];
  if (first) return first;
  return owner.you ? 'you' : 'an owner';
}

/**
 * Where a note came from, and when: "Neo noticed this in the readings, Oct 3",
 * or "From Sam, Oct 10". A former owner's note says "From a former owner",
 * never their name (Mike's decision, 2026-10-10).
 */
export function noteSource(
  note: BoatNote,
  owners: readonly BoatOwner[] | null,
  now: Date = new Date()
): string {
  if (note.origin === 'observed') {
    return `Neo noticed this in the readings, ${noteDay(note.observedAt, now)}`;
  }
  const who = note.authorIsFormerOwner
    ? 'a former owner'
    : (ownerWord(note.authorUser, owners) ?? 'a former owner');
  // When the owner gave the words: a correct dates the note afresh (ADR 0373).
  return `From ${who}, ${noteDay(note.observedAt, now)}`;
}

/** "Sam confirmed it, Oct 11", for an observed note an owner said is right. */
export function noteConfirmation(
  note: BoatNote,
  owners: readonly BoatOwner[] | null,
  now: Date = new Date()
): string | null {
  if (note.origin !== 'observed' || note.confirmedAt === null) return null;
  const who = ownerWord(note.confirmedBy, owners) ?? 'A former owner';
  const name = who === 'you' ? 'You' : who === 'an owner' ? 'An owner' : who;
  return `${name} confirmed it, ${noteDay(note.confirmedAt, now)}`;
}

/** What the page says plainly about a note Neo does not use as it is, or null. */
export function noteStateText(note: BoatNote): string | null {
  if (note.disputedAt !== null)
    return 'The readings seem to disagree with this. Is it still right?';
  switch (note.status) {
    case 'contradicted':
      return 'The readings no longer agree with this, so Neo has stopped using it.';
    case 'stale':
      return 'Its readings went quiet, so Neo has stopped using it.';
    case 'set_aside':
      return 'Set aside to make room for an owner’s note, so Neo has stopped using it.';
    default:
      return null;
  }
}

/** Whether the page offers Confirm: an observed note no owner confirmed, or one the readings dispute. */
export function canConfirm(note: BoatNote): boolean {
  if (note.disputedAt !== null) return true;
  return note.origin === 'observed' && (note.confirmedAt === null || note.status !== 'active');
}

const REFUSALS: Readonly<Record<string, string>> = {
  text_empty: 'Write the note first.',
  text_too_long: `A note can be up to ${BOAT_NOTE_TEXT_MAX} characters.`,
  text_secret: 'A note can’t hold passwords, codes, keys, or card or ID numbers.',
  text_instruction: 'Write a plain fact about the boat, not an instruction.',
  text_transient:
    'Notes keep what stays true of the boat. Leave out words like “now” or “today”, and clock times.',
  area_full:
    'This part of the boat already has as many notes as it keeps, all from its owners. Forget one first.',
  boat_full:
    'This boat already has as many notes as it keeps, all from its owners. Forget one first.',
  nothing_to_undo: 'There’s nothing of yours left to undo on that note.',
  note_exists:
    'There’s already a note of that kind on this. Correct that one, or pick another kind of fact.',
  bad_subject: 'Pick what the note is about from the list.',
  note_not_found: 'That note is gone. Another owner may have forgotten it.',
};

/** The sentence for a refused change. */
export function noteRefusalText(code: string): string {
  return REFUSALS[code] ?? 'Couldn’t change that note. Nothing changed. Try again.';
}

// ----- Grouping ---------------------------------------------------------------------------

/** The equipment an owner may file a note under, by topic: batteries, engines and gearboxes, tanks. */
const TOPIC_FAMILIES: Readonly<Record<BoatNoteArea, readonly EquipmentEntry['family'][]>> = {
  electrical: ['battery'],
  mechanical: ['engine', 'gearbox'],
  electronics: [],
  boards: [],
  general: ['tank'],
};

/** What a new note in this topic can be about, besides the topic as a whole, in the list's order. */
export function equipmentChoices(
  area: BoatNoteArea,
  list: readonly EquipmentEntry[]
): Array<{ instance: string; label: string }> {
  const families = TOPIC_FAMILIES[area];
  return list
    .filter((e) => families.includes(e.family))
    .map((e) => ({ instance: e.instance, label: e.label }));
}

/** The piece of equipment a note's subject names, by its key or one of its exact paths. */
export function equipmentOfNote(
  note: Pick<BoatNote, 'subject'>,
  list: readonly EquipmentEntry[]
): EquipmentEntry | null {
  const subject = note.subject;
  if (subject === null) return null;
  return list.find((e) => e.instance === subject || e.paths.includes(subject)) ?? null;
}

export interface NoteTopic {
  area: BoatNoteArea;
  heading: string;
  /** Notes on the area as a whole, or on something the page shows no equipment for. */
  loose: BoatNote[];
  /** Notes filed under a piece of equipment the page shows, in the list's order. */
  equipment: Array<{ instance: string; label: string; notes: BoatNote[] }>;
}

/**
 * The page's notebook: the notes Neo uses or the readings dispute, by topic,
 * each filed under its piece of equipment when the page shows it, the disputed
 * after the rest; then the
 * notes out of date (their readings went quiet, or an owner's note took their
 * room), apart and muted. The cloud's order holds within each.
 */
export function notebookSections(
  notes: readonly BoatNote[],
  equipment: readonly EquipmentEntry[]
): { topics: NoteTopic[]; outOfDate: BoatNote[] } {
  // The notes Neo uses first, then those the readings disagree with; the
  // cloud's order (surest first) within each.
  const unsettled = (n: BoatNote) => (n.status === 'contradicted' || n.disputedAt !== null ? 1 : 0);
  const current = notes
    .filter((n) => n.status === 'active' || n.status === 'contradicted')
    .map((n, i) => ({ n, i }))
    .sort((a, b) => unsettled(a.n) - unsettled(b.n) || a.i - b.i)
    .map(({ n }) => n);
  const topics: NoteTopic[] = [];
  for (const { area, heading } of NOTE_TOPICS) {
    const inArea = current.filter((n) => n.area === area);
    if (inArea.length === 0) continue;
    const loose: BoatNote[] = [];
    const filed = new Map<string, BoatNote[]>();
    for (const note of inArea) {
      const entry = equipmentOfNote(note, equipment);
      if (entry === null) loose.push(note);
      else filed.set(entry.instance, [...(filed.get(entry.instance) ?? []), note]);
    }
    topics.push({
      area,
      heading,
      loose,
      equipment: equipment
        .filter((e) => filed.has(e.instance))
        .map((e) => ({ instance: e.instance, label: e.label, notes: filed.get(e.instance)! })),
    });
  }
  return {
    topics,
    outOfDate: notes.filter((n) => n.status === 'stale' || n.status === 'set_aside'),
  };
}
